<template>
  <div class="schulungsplan-container">
    <div class="d-flex flex-wrap justify-space-between align-center mb-6 gap-3">
      <h2 class="text-h5 text-md-h4 font-weight-bold white--text">Schulungsplan</h2>

      <div class="d-flex align-center bg-darker rounded-pill pa-1">
        <v-btn dark text rounded small :class="{ 'gold-text': viewMode === 'list' }" @click="viewMode = 'list'">
          <v-icon left small>mdi-view-grid-outline</v-icon> Liste
        </v-btn>
        <v-btn dark text rounded small :class="{ 'gold-text': viewMode === 'calendar' }" @click="viewMode = 'calendar'">
          <v-icon left small>mdi-calendar-month</v-icon> Kalender
        </v-btn>
      </div>
    </div>

    <!-- Loading / Empty -->
    <v-row v-if="loading">
      <v-col v-for="n in 3" :key="n" cols="12" md="6" lg="4">
        <v-skeleton-loader type="card" dark></v-skeleton-loader>
      </v-col>
    </v-row>
    <div v-else-if="!termine.length" class="text-center pa-12 grey--text">
      <v-icon size="64" color="grey darken-2">mdi-calendar-blank</v-icon>
      <h3 class="text-h6 mt-4">Aktuell sind keine Schulungen ausgeschrieben.</h3>
    </div>

    <!-- Listen-Ansicht -->
    <v-row v-else-if="viewMode === 'list'">
      <v-col cols="12" md="6" lg="4" v-for="(termin, idx) in termine" :key="termin._id">
        <v-card
          class="termin-card rounded-xl overflow-hidden h-100"
          dark
          elevation="0"
          :style="{ animationDelay: idx * 0.05 + 's' }"
          @click="openTermin(termin)">
          <div class="card-visual">
            <div class="visual-pattern"></div>
            <div class="visual-glow"></div>

            <!-- Date Badge mit Tag/Monat -->
            <div class="date-block">
              <div class="date-day">{{ day(termin.startsAt) }}</div>
              <div class="date-month">{{ monthShort(termin.startsAt) }}</div>
            </div>

            <div class="visual-meta">
              <v-icon size="22" color="#D4AF37" class="mr-2">mdi-calendar-star</v-icon>
              <div>
                <div class="visual-time">{{ formatTime(termin.startsAt) }} – {{ formatTime(termin.endsAt) }}</div>
                <div class="visual-location text-truncate" v-if="termin.location">
                  <v-icon x-small color="grey" class="mr-1">mdi-map-marker</v-icon>{{ termin.location }}
                </div>
              </div>
            </div>

            <!-- Phase Pill -->
            <div class="phase-pill px-3 py-1 text-caption font-weight-bold rounded-pill" :class="getPhaseColorClass(getPhase(termin))">
              {{ getPhase(termin) }}
            </div>

            <!-- Registration Badge -->
            <v-chip
              v-if="termin.myRegistration"
              class="reg-badge"
              :color="termin.myRegistration.status === 'waiting_list' ? 'orange' : 'green'"
              small
              text-color="white">
              <v-icon left small>mdi-check</v-icon>
              {{ termin.myRegistration.status === "waiting_list" ? "Warteliste" : "Angemeldet" }}
            </v-chip>
          </div>

          <v-card-text class="card-body">
            <h3 class="text-h6 font-weight-bold white--text mb-2 card-title">{{ termin.title }}</h3>
            <p v-if="termin.description" class="text-caption grey--text mb-4 desc-clip">{{ termin.description }}</p>

            <v-divider class="my-3 border-white-05"></v-divider>

            <div class="d-flex justify-space-between align-center">
              <div class="d-flex align-center text-caption grey--text">
                <v-icon small color="#D4AF37" class="mr-1">mdi-account-multiple-outline</v-icon>
                <span
                  ><span class="gold-text font-weight-bold">{{ termin.registeredCount || 0 }}</span> / {{ termin.maxParticipants }}</span
                >
                <span class="grey--text ml-1">Plätze</span>
              </div>
              <v-btn icon small color="#D4AF37" class="arrow-btn">
                <v-icon>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Kalender-Ansicht -->
    <v-card v-else class="calendar-card rounded-xl pa-3 pa-md-5" dark elevation="0">
      <div class="d-flex align-center mb-4 flex-wrap gap-2">
        <v-btn icon @click="prevMonth"><v-icon>mdi-chevron-left</v-icon></v-btn>
        <h3 class="text-h6 font-weight-bold mx-2">{{ monthLabel }}</h3>
        <v-btn icon @click="nextMonth"><v-icon>mdi-chevron-right</v-icon></v-btn>
        <v-spacer />
        <v-btn small outlined color="#D4AF37" rounded @click="goToToday">Heute</v-btn>
      </div>

      <div class="calendar-grid">
        <div class="calendar-day-name" v-for="d in dayNames" :key="d">{{ d }}</div>
        <div
          v-for="(cell, i) in calendarCells"
          :key="i"
          class="calendar-cell"
          :class="{
            'is-other-month': !cell.inMonth,
            'is-today': cell.isToday,
            'has-events': cell.events.length > 0,
          }">
          <div class="cell-day">{{ cell.day }}</div>
          <div class="cell-events">
            <div
              v-for="ev in cell.events"
              :key="ev._id"
              class="cell-event"
              :class="phaseColorClass(getPhase(ev))"
              :title="ev.title"
              @click="openTermin(ev)">
              <span class="ev-time">{{ formatTimeShort(ev.startsAt) }}</span>
              <span class="ev-title">{{ ev.title }}</span>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import api from "@/api"

const DAY_NAMES = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]

export default {
  name: "Schulungsplan",
  data() {
    return {
      viewMode: "list",
      termine: [],
      loading: false,
      cursor: this.firstOfMonth(new Date()),
      dayNames: DAY_NAMES,
    }
  },
  computed: {
    monthLabel() {
      return this.cursor.toLocaleDateString("de-CH", { month: "long", year: "numeric" })
    },
    calendarCells() {
      const year = this.cursor.getFullYear()
      const month = this.cursor.getMonth()
      const first = new Date(year, month, 1)
      // Mo = 1 ... So = 0 -> Wir wollen Mo-basiert
      const firstWeekday = (first.getDay() + 6) % 7
      const daysInMonth = new Date(year, month + 1, 0).getDate()

      const cells = []
      const today = new Date()
      const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

      // Vorheriger Monat als Padding
      const prevDays = new Date(year, month, 0).getDate()
      for (let i = firstWeekday - 1; i >= 0; i--) {
        const d = new Date(year, month - 1, prevDays - i)
        cells.push(this.buildCell(d, false, isSameDay(d, today)))
      }
      // Aktueller Monat
      for (let i = 1; i <= daysInMonth; i++) {
        const d = new Date(year, month, i)
        cells.push(this.buildCell(d, true, isSameDay(d, today)))
      }
      // Auffüllen auf volle Wochen (max 42)
      let nextDay = 1
      while (cells.length % 7 !== 0 || cells.length < 42) {
        const d = new Date(year, month + 1, nextDay++)
        cells.push(this.buildCell(d, false, isSameDay(d, today)))
        if (cells.length >= 42) break
      }
      return cells
    },
  },
  mounted() {
    this.loadTermine()
  },
  methods: {
    firstOfMonth(d) {
      return new Date(d.getFullYear(), d.getMonth(), 1)
    },
    prevMonth() {
      this.cursor = new Date(this.cursor.getFullYear(), this.cursor.getMonth() - 1, 1)
    },
    nextMonth() {
      this.cursor = new Date(this.cursor.getFullYear(), this.cursor.getMonth() + 1, 1)
    },
    goToToday() {
      this.cursor = this.firstOfMonth(new Date())
    },
    buildCell(d, inMonth, isToday) {
      const events = (this.termine || []).filter((t) => {
        if (!t.startsAt) return false
        const s = new Date(t.startsAt)
        return s.getFullYear() === d.getFullYear() && s.getMonth() === d.getMonth() && s.getDate() === d.getDate()
      })
      return { date: d, day: d.getDate(), inMonth, isToday, events }
    },
    async loadTermine() {
      this.loading = true
      try {
        this.termine = (await api.akademie.fetchTermine()) || []
      } catch (err) {
        console.error("Fehler beim Laden der Termine:", err)
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      if (!date) return ""
      return new Date(date).toLocaleDateString("de-CH") + ", " + new Date(date).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    formatTime(date) {
      if (!date) return ""
      return new Date(date).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    monthShort(d) {
      if (!d) return ""
      return new Date(d).toLocaleDateString("de-CH", { month: "short" }).replace(".", "").toUpperCase()
    },
    day(d) {
      if (!d) return ""
      return new Date(d).getDate()
    },
    formatTimeShort(date) {
      if (!date) return ""
      return new Date(date).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    getPhase(termin) {
      if (!termin.registrationStart || !termin.registrationEnd) return "Unbekannt"
      const now = new Date()
      const start = new Date(termin.registrationStart)
      const end = new Date(termin.registrationEnd)
      if (now < start) return "Geplant"
      if (now > end) return "Geschlossen"
      return "Geöffnet"
    },
    getPhaseColorClass(phase) {
      if (phase === "Geöffnet") return "bg-success"
      if (phase === "Geplant") return "bg-warning black--text"
      return "bg-error"
    },
    phaseColorClass(phase) {
      if (phase === "Geöffnet") return "ev-success"
      if (phase === "Geplant") return "ev-warning"
      return "ev-error"
    },
    openTermin(termin) {
      this.$router.push(`/akademie/schulungen/${termin._id}`)
    },
  },
}
</script>

<style scoped>
.schulungsplan-container {
  min-height: 80vh;
}

.bg-darker {
  background: rgba(0, 0, 0, 0.3);
}
.gold-text {
  color: #d4af37 !important;
}
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}

.termin-card {
  background: rgba(30, 30, 30, 0.85) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  animation: cardIn 0.5s ease both;
}
@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.termin-card:hover {
  transform: translateY(-4px);
  border-color: rgba(212, 175, 55, 0.45);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.5), 0 0 16px rgba(212, 175, 55, 0.12);
}

/* === VISUAL HEADER === */
.card-visual {
  position: relative;
  height: 150px;
  background: linear-gradient(135deg, rgba(35, 30, 20, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 14px 16px;
  overflow: hidden;
}
.visual-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.05) 1px, transparent 0);
  background-size: 14px 14px;
  pointer-events: none;
}
.visual-glow {
  position: absolute;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  filter: blur(60px);
  background: rgba(212, 175, 55, 0.16);
  top: -80px;
  right: -60px;
  pointer-events: none;
  transition: transform 0.6s ease;
}
.termin-card:hover .visual-glow {
  transform: translate(-10px, 10px) scale(1.1);
}

.date-block {
  position: absolute;
  top: 14px;
  left: 14px;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(212, 175, 55, 0.3);
  border-radius: 12px;
  padding: 6px 12px;
  text-align: center;
  min-width: 56px;
  backdrop-filter: blur(6px);
  z-index: 1;
}
.date-day {
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
  line-height: 1;
}
.date-month {
  font-size: 0.65rem;
  color: #d4af37;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-top: 2px;
  font-weight: 600;
}

.visual-meta {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  padding: 8px 10px;
  min-width: 0;
}
.visual-time {
  font-size: 0.85rem;
  font-weight: 700;
  color: white;
  font-variant-numeric: tabular-nums;
}
.visual-location {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
}

.position-relative {
  position: relative;
}
.position-absolute {
  position: absolute;
}

.phase-pill {
  position: absolute;
  top: 14px;
  right: 14px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  backdrop-filter: blur(4px);
  z-index: 1;
  font-size: 0.65rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.reg-badge {
  position: absolute;
  top: 50px;
  right: 14px;
  backdrop-filter: blur(4px);
  z-index: 1;
}

/* === BODY === */
.card-body {
  padding: 14px 16px 14px;
  display: flex;
  flex-direction: column;
  flex: 1;
}
.card-title {
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.6em;
}
.desc-clip {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.arrow-btn {
  transition: transform 0.25s ease;
}
.termin-card:hover .arrow-btn {
  transform: translateX(4px);
}

.bg-success {
  background: #4caf50 !important;
}
.bg-warning {
  background: #ffc107 !important;
}
.bg-error {
  background: #f44336 !important;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

@media (max-width: 600px) {
  .schulungsplan-container {
    padding: 0 4px;
  }
}

/* === KALENDER === */
.calendar-card {
  background: rgba(30, 30, 30, 0.85) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}
.calendar-day-name {
  text-align: center;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(212, 175, 55, 0.8);
  padding: 8px 0;
}
.calendar-cell {
  background: rgba(20, 20, 20, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  min-height: 92px;
  padding: 6px;
  position: relative;
  display: flex;
  flex-direction: column;
  transition: border-color 0.2s;
}
.calendar-cell.is-other-month {
  opacity: 0.35;
}
.calendar-cell.is-today {
  border-color: #d4af37;
  box-shadow: inset 0 0 0 1px rgba(212, 175, 55, 0.4);
}
.calendar-cell.has-events {
  background: rgba(30, 30, 30, 0.9);
}
.cell-day {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
  margin-bottom: 4px;
}
.cell-events {
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow: hidden;
}
.cell-event {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-left: 3px solid transparent;
  background: rgba(255, 255, 255, 0.05);
  color: white;
}
.cell-event:hover {
  background: rgba(212, 175, 55, 0.2);
}
.ev-time {
  color: rgba(255, 255, 255, 0.7);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}
.ev-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ev-success {
  border-left-color: #4caf50;
}
.ev-warning {
  border-left-color: #ffc107;
}
.ev-error {
  border-left-color: #f44336;
}

@media (max-width: 768px) {
  .calendar-cell {
    min-height: 64px;
    padding: 4px;
  }
  .ev-title {
    display: none;
  }
  .cell-event {
    justify-content: center;
  }
}
</style>
